'use client'

import { useEffect, useRef, type RefObject } from 'react'

interface UseOutsidePressOptions {
  /** Skip subscribing entirely — pass the disclosure's own open state. */
  enabled?: boolean
}

/**
 * Runs `onOutsidePress` when a pointer press lands outside `containerRef`.
 *
 * Register this in the component that **owns** the state being dismissed, on a
 * ref around every disclosure that press should close — never in a leaf. A leaf
 * can only test containment against its own box, so it reports "outside" for
 * presses that are still inside its owner's box; if that report resolves to
 * state shared with a sibling (ProFinder keeps both search dropdowns in one
 * exclusive-open value) the leaf silently dismisses the sibling. That is exactly
 * how pressing a ProFinder service suggestion came to close the service
 * typeahead — see docs/adr/0003-outside-press-belongs-to-the-owner.md.
 *
 * `mousedown`, not `click`, so a dismissal isn't deferred until the button is
 * released — which is also why the containment check has to be generous: this
 * fires *before* the press's `click`, so anything wrongly treated as outside is
 * unmounted out from under a click that was about to land.
 */
export function useOutsidePress(
  containerRef: RefObject<HTMLElement | null>,
  onOutsidePress: () => void,
  { enabled = true }: UseOutsidePressOptions = {}
): void {
  // Held in a ref so callers can pass an inline closure without resubscribing
  // the listener on every render.
  const handlerRef = useRef(onOutsidePress)
  useEffect(() => {
    handlerRef.current = onOutsidePress
  }, [onOutsidePress])

  useEffect(() => {
    if (!enabled) return
    const onMouseDown = (event: MouseEvent) => {
      const container = containerRef.current
      if (!container || container.contains(event.target as Node)) return
      handlerRef.current()
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [containerRef, enabled])
}
